import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, LoadingState, ErrorState, Select, TextInput, SlateButton } from "@/components/common/UI";
import { ClipboardList, Building, FileText, Download, Trophy, AlertTriangle, Sparkles, TrendingUp, Wallet } from "lucide-react";
import { toast } from "sonner";
import { fmtINR, fmtPct } from "@/lib/format";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const RFQ_STATUS_LABELS = {
    DRAFT: "Draft", PENDING_DH: "Pending DH", PENDING_DIRECTOR: "Pending Director",
    HELD: "Held", APPROVED: "Approved", DIRECTOR_APPROVED: "Director Approved",
    REJECTED: "Rejected", WITHDRAWN: "Withdrawn",
};
const RFQ_STATUS_ORDER = Object.keys(RFQ_STATUS_LABELS);

export default function Reports() {
    const navigate = useNavigate();
    const [plants, setPlants] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [plantId, setPlantId] = useState("");
    const [customerId, setCustomerId] = useState("");
    const [view, setView] = useState("corporate"); // corporate | unit | customer
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    // Quality signals already built elsewhere (Win-Rate, Variance Report,
    // Anomaly Detector) — wired in here as read-only summary cards rather
    // than duplicating their logic. Each loads independently so one failing
    // (e.g. a role gap) never blocks the rest of the page.
    const [winRate, setWinRate] = useState(null);
    const [varianceSummary, setVarianceSummary] = useState(null);
    const [anomalySummary, setAnomalySummary] = useState(null);

    useEffect(() => {
        api.get("/plants").then((r) => setPlants(r.data)).catch(() => {});
        api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
        api.get("/rfqs/win-rate").then((r) => setWinRate(r.data)).catch(() => {});
        api.get("/zbcs/variance-report").then((r) => setVarianceSummary(r.data)).catch(() => {});
        api.get("/dashboard/anomalies").then((r) => setAnomalySummary(r.data)).catch(() => {});
    }, []);

    const buildParams = useCallback(() => {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (plantId) params.plant_id = plantId;
        if (customerId) params.customer_id = customerId;
        return params;
    }, [dateFrom, dateTo, plantId, customerId]);

    const load = useCallback(() => {
        setLoading(true);
        api.get("/reports/pipeline", { params: buildParams() })
            .then((r) => setData(r.data))
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [buildParams]);

    useEffect(() => { load(); }, [load]);

    const downloadExport = async () => {
        setExporting(true);
        try {
            const res = await api.get("/reports/pipeline/export.xlsx", {
                params: buildParams(), responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "CostIQ_Reports_Pipeline.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Reports Excel downloaded");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setExporting(false);
        }
    };

    return (
        <div data-testid="reports-page">
            <PageHeader
                eyebrow="MODULE · ANALYTICS"
                title="Reports"
                subtitle="Parameter-driven view of the ZBC and RFQ pipeline, Settlement claims, and trend charts, sliced by date range, plant, and customer, at corporate, unit, or customer level."
                actions={
                    <SlateButton variant="outline" onClick={downloadExport} disabled={exporting} data-testid="reports-export-btn">
                        <Download className="h-4 w-4" /> {exporting ? "Exporting…" : "Export (.xlsx)"}
                    </SlateButton>
                }
            />

            <Card className="p-3 mb-4">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    <TextInput type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-[12px]" data-testid="reports-date-from" />
                    <TextInput type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-[12px]" data-testid="reports-date-to" />
                    <Select value={plantId} onChange={(e) => setPlantId(e.target.value)} className="h-8 text-[12px]" data-testid="reports-plant">
                        <option value="">All plants</option>
                        {plants.map((p) => <option key={p.id} value={p.id}>{p.code}</option>)}
                    </Select>
                    <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="h-8 text-[12px]" data-testid="reports-customer">
                        <option value="">All customers</option>
                        {customers.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                    </Select>
                    <Select value={view} onChange={(e) => setView(e.target.value)} className="h-8 text-[12px]" data-testid="reports-view-toggle">
                        <option value="corporate">Corporate view</option>
                        <option value="unit">By Unit (plant)</option>
                        <option value="customer">By Customer</option>
                    </Select>
                </div>
            </Card>

            {error && <ErrorState message={error} onRetry={load} />}
            {loading && !data && <LoadingState />}

            {data && (
                <>
                    <SectionCard icon={ClipboardList} title="ZBC Pipeline" subtitle="Received in the selected window, and their current status.">
                        {view === "corporate" && (
                            <div className="grid grid-cols-3 gap-3">
                                <StatTile label="RECEIVED" value={data.zbc.corporate.received} tone="slate" />
                                <StatTile label="CLEARED (VERIFIED)" value={data.zbc.corporate.verified} tone="emerald" />
                                <StatTile label="PENDING (DRAFT)" value={data.zbc.corporate.pending} tone="amber" />
                            </div>
                        )}
                        {view === "unit" && (
                            <UnitTable
                                rows={data.zbc.by_plant}
                                columns={[
                                    { key: "received", label: "Received" },
                                    { key: "verified", label: "Cleared" },
                                    { key: "pending", label: "Pending" },
                                ]}
                            />
                        )}
                        {view === "customer" && (
                            <CustomerTable
                                rows={data.zbc.by_customer}
                                columns={[
                                    { key: "received", label: "Received" },
                                    { key: "verified", label: "Cleared" },
                                    { key: "pending", label: "Pending" },
                                ]}
                            />
                        )}
                    </SectionCard>

                    <SectionCard icon={FileText} title="RFQ Approval Pipeline" subtitle="Pending at each approval level, and approvals within the selected date range." className="mt-4">
                        {view === "corporate" && (
                            <>
                                <div className="grid grid-cols-2 gap-3 mb-3">
                                    <StatTile label="TOTAL RFQs (IN SCOPE)" value={data.rfq.corporate.total} tone="slate" />
                                    <StatTile label="APPROVED IN RANGE" value={data.rfq.corporate.approved_in_range} tone="emerald" />
                                </div>
                                <StatusBreakdown byStatus={data.rfq.corporate.by_status} />
                            </>
                        )}
                        {view === "unit" && (
                            <UnitTable
                                rows={data.rfq.by_plant}
                                columns={[
                                    { key: "total", label: "Total" },
                                    ...RFQ_STATUS_ORDER.map((s) => ({ key: `by_status.${s}`, label: RFQ_STATUS_LABELS[s] })),
                                    { key: "approved_in_range", label: "Approved (range)" },
                                ]}
                            />
                        )}
                        {view === "customer" && (
                            <CustomerTable
                                rows={data.rfq.by_customer}
                                columns={[
                                    { key: "total", label: "Total" },
                                    ...RFQ_STATUS_ORDER.map((s) => ({ key: `by_status.${s}`, label: RFQ_STATUS_LABELS[s] })),
                                    { key: "approved_in_range", label: "Approved (range)" },
                                ]}
                            />
                        )}
                        <div className="text-[11px] text-slate-400 mt-3">{data.rfq.note}</div>
                    </SectionCard>

                    <SectionCard icon={Wallet} title="Settlement Claims" subtitle="Director-approved (locked) settlements only — the same 'realized claim' scope the Director dashboard's quarterly sum uses, made filterable by date range and customer." className="mt-4">
                        {view !== "customer" ? (
                            <div className="grid grid-cols-4 gap-3">
                                <StatTile label="LOCKED SETTLEMENTS" value={data.settlement.corporate.count} tone="slate" />
                                <StatTile label="DMC (FULL PERIOD)" value={fmtINR(data.settlement.corporate.dmc_full_period)} tone="slate" />
                                <StatTile label="WEIGHT-GAP RECOVERY" value={fmtINR(data.settlement.corporate.weight_gap_recovery)} tone="slate" />
                                <StatTile label="TOTAL CLAIM" value={fmtINR(data.settlement.corporate.total_claim)} tone="emerald" />
                            </div>
                        ) : (
                            <CustomerTable
                                rows={data.settlement.by_customer}
                                columns={[
                                    { key: "count", label: "Settlements" },
                                    { key: "dmc_full_period", label: "DMC", money: true },
                                    { key: "weight_gap_recovery", label: "Weight-Gap", money: true },
                                    { key: "total_claim", label: "Total Claim", money: true },
                                ]}
                            />
                        )}
                        <div className="text-[11px] text-slate-400 mt-3">{data.settlement.note}</div>
                    </SectionCard>

                    <SectionCard icon={TrendingUp} title="Trend" subtitle="ZBC and RFQ volume over time, within the selected scope (not month-filtered, so the shape of history stays visible even with no date range set)." className="mt-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <TrendChart
                                title="ZBC · Received vs Cleared"
                                data={data.zbc.trend}
                                lines={[
                                    { key: "received", name: "Received", color: "#64748b" },
                                    { key: "verified", name: "Cleared", color: "hsl(18, 65%, 47%)" },
                                ]}
                            />
                            <TrendChart
                                title="RFQ · Received vs Approved"
                                data={data.rfq.trend}
                                lines={[
                                    { key: "received", name: "Received", color: "#64748b" },
                                    { key: "approved", name: "Approved", color: "#10b981" },
                                ]}
                            />
                        </div>
                    </SectionCard>

                    <SectionCard icon={Sparkles} title="Quality Signals" subtitle="Portfolio-wide — these already run elsewhere in CostIQ and aren't scoped by the filters above; click through for the full, filterable view." className="mt-4">
                        <div className="grid grid-cols-3 gap-3">
                            <button onClick={() => navigate("/rfq")} className="text-left" data-testid="reports-winrate-tile">
                                <StatTile
                                    label="RFQ WIN RATE" icon={Trophy}
                                    value={winRate?.overall?.win_rate_pct != null ? fmtPct(winRate.overall.win_rate_pct) : "—"}
                                    tone="emerald"
                                    sub={winRate ? `${winRate.overall.won} won · ${winRate.overall.lost} lost · ${winRate.overall.partial} partial` : "Loading…"}
                                />
                            </button>
                            <button onClick={() => navigate("/zbc/variance-report")} className="text-left" data-testid="reports-variance-tile">
                                <StatTile
                                    label="VARIANCE FLAGS" icon={AlertTriangle}
                                    value={varianceSummary ? (varianceSummary.counts.RED || 0) + (varianceSummary.counts.FLAG || 0) : "—"}
                                    tone={varianceSummary && ((varianceSummary.counts.RED || 0) + (varianceSummary.counts.FLAG || 0)) > 0 ? "amber" : "slate"}
                                    sub={varianceSummary ? `${varianceSummary.counts.RED || 0} RED · ${varianceSummary.counts.FLAG || 0} FLAG of ${varianceSummary.total}` : "Loading…"}
                                />
                            </button>
                            <button onClick={() => navigate("/")} className="text-left" data-testid="reports-anomaly-tile">
                                <StatTile
                                    label="ANOMALIES FLAGGED" icon={Sparkles}
                                    value={anomalySummary?.anomaly_count ?? "—"}
                                    tone={anomalySummary?.anomaly_count > 0 ? "amber" : "slate"}
                                    sub={anomalySummary ? `of ${anomalySummary.total_verified_zbcs} verified ZBCs` : "Loading…"}
                                />
                            </button>
                        </div>
                    </SectionCard>
                </>
            )}
        </div>
    );
}

function SectionCard({ icon: Icon, title, subtitle, children, className = "" }) {
    return (
        <Card className={`p-5 ${className}`}>
            <div className="flex items-center gap-2 mb-1">
                <Icon className="h-4 w-4 text-[hsl(var(--copper))]" />
                <div className="text-[14px] font-semibold text-slate-800">{title}</div>
            </div>
            <p className="text-[12px] text-slate-500 mb-4">{subtitle}</p>
            {children}
        </Card>
    );
}

function StatTile({ label, value, tone = "slate", icon: Icon, sub }) {
    const toneMap = {
        slate: "bg-slate-50 border-slate-200 text-slate-800",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
        amber: "bg-amber-50 border-amber-200 text-amber-700",
    };
    return (
        <div className={`rounded-md border p-3 ${toneMap[tone] || toneMap.slate}`}>
            <div className="text-[10.5px] uppercase tracking-widest font-semibold opacity-70 flex items-center gap-1">
                {Icon && <Icon className="h-3 w-3" />} {label}
            </div>
            <div className="text-[22px] font-mono tabular-nums font-semibold mt-1">{value}</div>
            {sub && <div className="text-[11px] font-normal opacity-80 mt-0.5 normal-case tracking-normal">{sub}</div>}
        </div>
    );
}

function StatusBreakdown({ byStatus }) {
    return (
        <div className="grid grid-cols-4 gap-2">
            {RFQ_STATUS_ORDER.map((s) => (
                <div key={s} className="rounded-md border border-slate-200 p-2.5 text-center">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">{RFQ_STATUS_LABELS[s]}</div>
                    <div className="font-mono tabular-nums text-[16px] font-semibold text-slate-800 mt-0.5">{byStatus[s] ?? 0}</div>
                </div>
            ))}
        </div>
    );
}

function _get(obj, path) {
    return path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
}

function UnitTable({ rows, columns }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="flex items-center gap-2 text-[12px] text-slate-500 py-6 justify-center">
                <Building className="h-4 w-4" /> No plants have data in the selected scope.
            </div>
        );
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
                <thead>
                    <tr className="border-b border-slate-200 text-slate-500">
                        <th className="text-left py-1.5 pr-3">Plant</th>
                        {columns.map((c) => <th key={c.key} className="text-right py-1.5 px-2 whitespace-nowrap">{c.label}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.plant_id} className="border-b border-slate-100">
                            <td className="py-1.5 pr-3 font-mono font-semibold whitespace-nowrap">{row.plant_code}</td>
                            {columns.map((c) => (
                                <td key={c.key} className="py-1.5 px-2 text-right font-mono tabular-nums">{_get(row, c.key) ?? 0}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CustomerTable({ rows, columns }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="flex items-center gap-2 text-[12px] text-slate-500 py-6 justify-center">
                <Building className="h-4 w-4" /> No customers have data in the selected scope.
            </div>
        );
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
                <thead>
                    <tr className="border-b border-slate-200 text-slate-500">
                        <th className="text-left py-1.5 pr-3">Customer</th>
                        {columns.map((c) => <th key={c.key} className="text-right py-1.5 px-2 whitespace-nowrap">{c.label}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.customer_id} className="border-b border-slate-100">
                            <td className="py-1.5 pr-3 whitespace-nowrap">
                                <span className="font-mono font-semibold">{row.customer_code}</span>
                                {row.customer_name && <span className="text-slate-500 ml-1.5">{row.customer_name}</span>}
                            </td>
                            {columns.map((c) => {
                                const v = _get(row, c.key) ?? 0;
                                return (
                                    <td key={c.key} className="py-1.5 px-2 text-right font-mono tabular-nums">
                                        {c.money ? fmtINR(v) : v}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function TrendChart({ title, data, lines }) {
    if (!data || data.length < 2) {
        return (
            <div className="rounded-md border border-slate-200 p-4">
                <div className="eyebrow text-slate-500 mb-2">{title}</div>
                <div className="flex items-center justify-center py-10 text-[12px] text-slate-400">
                    Needs at least 2 months of data to plot a trend.
                </div>
            </div>
        );
    }
    return (
        <div className="rounded-md border border-slate-200 p-4">
            <div className="eyebrow text-slate-500 mb-2">{title}</div>
            <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="#94a3b8" allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {lines.map((l) => (
                        <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} strokeWidth={2} dot={{ r: 2 }} />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}
